import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import {
  autodetectVendorByCui,
  autoFetchVendorLogo,
  deleteVendorDocument,
  getInvoiceLines,
  getInvoiceProofUrl,
  getInvoiceStatusAudit,
  getPayments,
  getVendor,
  getVendorDocumentFileUrl,
  getVendorLogoUrl,
  getVendorDocuments,
  getVendorInvoices,
  getVendorSummary,
  reuploadVendorDocumentFile,
  updateVendor,
  uploadVendorLogoFromUrl,
  uploadVendorDocument,
} from "./services/scadentarFurnizoriApi";
import "./styles/scadentarFurnizori.css";

const docTypes = ["NDA", "CONTRACT", "KYC", "OTHER"];
const vendorScopeOptions = [
  { value: "DOMESTIC", label: "Romania" },
  { value: "INTRA_EU", label: "Intracomunitar" },
  { value: "NON_EU", label: "Extracomunitar" },
];
const vendorCategoryOptions = [
  { value: "SERVICII", label: "Servicii" },
  { value: "BUNURI", label: "Bunuri" },
  { value: "IMPORT_BUNURI", label: "Import bunuri" },
  { value: "IMPORT_SERVICII", label: "Import servicii" },
  { value: "UTILITATI", label: "Utilitati" },
  { value: "CHIRIE", label: "Chirie" },
  { value: "SOFTWARE_SAAS", label: "Software / SaaS" },
];

const SCADENTAR_FURNIZORI_RETURN_KEY = "scadentarFurnizori.returnState";
const SCADENTAR_FURNIZORI_RETURN_MAX_AGE_MS = 30 * 60 * 1000;

const VENDOR_AUTOFILL_FIELDS = [
  "denumire",
  "cod_fiscal",
  "reg_comertului",
  "adresa",
  "localitate",
  "judet",
  "tara",
  "persoana_contact",
  "email",
  "telefon",
  "website",
  "banca",
  "iban",
  "vendor_scope",
  "vendor_category",
  "kyc_notes",
];

const VENDOR_SCOPE_VALUES = new Set(vendorScopeOptions.map((opt) => opt.value));
const VENDOR_CATEGORY_VALUES = new Set(vendorCategoryOptions.map((opt) => opt.value));

function readScadentarFurnizoriReturnState() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SCADENTAR_FURNIZORI_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    if (createdAt && Date.now() - createdAt > SCADENTAR_FURNIZORI_RETURN_MAX_AGE_MS) {
      window.sessionStorage.removeItem(SCADENTAR_FURNIZORI_RETURN_KEY);
      return null;
    }
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function normalizeAutofillValue(field, value) {
  if (value === undefined || value === null) return undefined;
  const normalized = String(value).trim();
  if (!normalized) return undefined;

  if (field === "vendor_scope") {
    return VENDOR_SCOPE_VALUES.has(normalized) ? normalized : undefined;
  }
  if (field === "vendor_category") {
    return VENDOR_CATEGORY_VALUES.has(normalized) ? normalized : undefined;
  }

  return normalized;
}

function mergeVendorAutofill(current, detected) {
  const next = { ...(current || {}) };
  VENDOR_AUTOFILL_FIELDS.forEach((field) => {
    const value = normalizeAutofillValue(field, detected?.[field]);
    if (value !== undefined) {
      next[field] = value;
    }
  });
  return next;
}

function buildAiAutofillMessage(result) {
  const source = String(result?.source || "").toUpperCase();
  if (source.includes("ANAF") && source.includes("OPENAI")) {
    return `Date completate din ANAF + OpenAI${result?.model ? ` (${result.model})` : ""}. Verifica si apasa Salveaza KYC.`;
  }
  if (source.includes("OPENAI")) {
    return `Date completate cu OpenAI${result?.used_web_search ? " + cautare web" : ""}${result?.model ? ` (${result.model})` : ""}. Verifica si apasa Salveaza KYC.`;
  }
  return "Date completate din ANAF. Verifica si apasa Salveaza KYC.";
}

function formatProfileDate(value) {
  const text = String(value || "").slice(0, 10);
  if (!text) return "-";
  const [year, month, day] = text.split("-");
  if (!year || !month || !day) return text;
  return `${day}.${month}.${year}`;
}

function formatProfileMoney(value, currency = "RON") {
  const amount = Number(value || 0);
  return `${amount.toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function getInvoiceDisplayNumber(invoice) {
  return invoice?.numar || invoice?.factura || invoice?.external_id || invoice?.id || "-";
}

function hasInvoiceProof(invoice) {
  return Boolean(
    invoice?.doc_factura_url ||
      invoice?.invoice_doc_url ||
      invoice?.document_url ||
      invoice?.doc_plata_url
  );
}

function ScadentarFurnizorProfilePage() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const scadentarReturnState = useMemo(
    () => location.state?.fromScadentarFurnizori || readScadentarFurnizoriReturnState(),
    [location.state]
  );
  const [vendor, setVendor] = useState(null);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState("");
  const [aiAutofillInfo, setAiAutofillInfo] = useState("");
  const [aiAutofillBusy, setAiAutofillBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docMeta, setDocMeta] = useState({ doc_type: "OTHER", title: "", note: "" });
  const [docFile, setDocFile] = useState(null);
  const [docBusyId, setDocBusyId] = useState(null);
  const [manualLogoUrl, setManualLogoUrl] = useState("");
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectedStatusAudit, setSelectedStatusAudit] = useState([]);
  const [invoicePanelLoading, setInvoicePanelLoading] = useState(false);
  const [invoicePanelError, setInvoicePanelError] = useState("");

  const isSpvImportedVendor = useMemo(() => {
    const vendorSource = String(vendor?.source || "").toUpperCase();
    const hasSpvInvoice = (invoices || []).some(
      (inv) => String(inv?.source || "").toUpperCase() === "SPV"
    );
    return vendorSource === "SPV" || hasSpvInvoice;
  }, [vendor?.source, invoices]);

  const loadAll = async () => {
    setError("");
    try {
      const [v, s, inv, docs] = await Promise.all([
        getVendor(vendorId),
        getVendorSummary(vendorId),
        getVendorInvoices(vendorId),
        getVendorDocuments(vendorId),
      ]);
      setVendor(v.data || null);
      setSummary(s.data || null);
      setInvoices(inv.data || []);
      setDocuments(docs.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Eroare incarcare furnizor");
    }
  };

  useEffect(() => {
    loadAll();
  }, [vendorId]);

  useEffect(() => {
    if (!selectedInvoice?.id) return;
    const fresh = (invoices || []).find(
      (invoice) => Number(invoice?.id) === Number(selectedInvoice.id)
    );
    if (fresh && fresh !== selectedInvoice) {
      setSelectedInvoice(fresh);
    }
    if (!fresh && invoices.length) {
      setSelectedInvoice(null);
    }
  }, [invoices, selectedInvoice]);

  useEffect(() => {
    if (!selectedInvoice?.id) {
      setSelectedInvoiceLines([]);
      setSelectedPayments([]);
      setSelectedStatusAudit([]);
      setInvoicePanelError("");
      return undefined;
    }

    let cancelled = false;
    setInvoicePanelLoading(true);
    setInvoicePanelError("");

    (async () => {
      try {
        const [linesRes, paymentsRes, statusAuditRes] = await Promise.all([
          getInvoiceLines(selectedInvoice.id),
          getPayments(selectedInvoice.id),
          getInvoiceStatusAudit(selectedInvoice.id, { limit: 20 }),
        ]);
        if (cancelled) return;
        setSelectedInvoiceLines(Array.isArray(linesRes?.data) ? linesRes.data : []);
        setSelectedPayments(Array.isArray(paymentsRes?.data) ? paymentsRes.data : []);
        setSelectedStatusAudit(Array.isArray(statusAuditRes?.data) ? statusAuditRes.data : []);
      } catch (err) {
        if (cancelled) return;
        setInvoicePanelError(
          err?.response?.data?.error ||
            err?.message ||
            "Nu am putut incarca detaliile facturii."
        );
      } finally {
        if (!cancelled) setInvoicePanelLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInvoice?.id]);

  const handleBackToScadentar = () => {
    if (scadentarReturnState) {
      navigate("/scadentar-furnizori", {
        state: { restoreScadentarFurnizori: scadentarReturnState },
      });
      return;
    }
    navigate("/scadentar-furnizori?tab=furnizori");
  };

  const handleAiAutofillVendor = async () => {
    const codFiscal = String(vendor?.cod_fiscal || "").trim();
    const denumire = String(vendor?.denumire || "").trim();
    if (!codFiscal && !denumire) {
      setError("Introdu codul fiscal sau denumirea firmei inainte de AI autocomplete.");
      setAiAutofillInfo("");
      return;
    }

    setAiAutofillBusy(true);
    setError("");
    setAiAutofillInfo("");
    try {
      const response = await autodetectVendorByCui(
        {
          cod_fiscal: codFiscal,
          denumire,
          tara: vendor?.tara || "",
          current: {
            ...vendor,
            denumire,
            cod_fiscal: codFiscal,
            tara: vendor?.tara || "",
          },
        },
        { timeout: 90000 }
      );
      const result = response?.data || {};
      setVendor((prev) => mergeVendorAutofill(prev, result?.data || {}));
      setAiAutofillInfo(buildAiAutofillMessage(result));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "AI autocomplete failed");
    } finally {
      setAiAutofillBusy(false);
    }
  };

  if (!vendor) {
    return (
      <Box className="sf-page">
        {error ? <Alert severity="error">{error}</Alert> : <Typography>Se incarca...</Typography>}
      </Box>
    );
  }

  return (
    <Box className="sf-page">
      {error && <Alert severity="error">{error}</Alert>}
      {aiAutofillInfo && <Alert severity="success">{aiAutofillInfo}</Alert>}

      <Paper className="sf-header-card">
        <Stack direction={{ xs: "column", md: "row" }} alignItems="center" spacing={2} justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={getVendorLogoUrl(vendor.id)} sx={{ width: 56, height: 56 }}>
              {(vendor.denumire || "?")[0]}
            </Avatar>
            <Box>
              <Typography className="sf-title">Fisa furnizor: {vendor.denumire}</Typography>
              <Typography className="sf-subtitle">KYC, documente, istoric facturi si plati</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} className="sf-profile-actions">
            <Tooltip title={scadentarReturnState?.selectedOverviewInvoiceId ? "Inapoi la factura" : "Inapoi la furnizori"}>
              <Button
                variant="outlined"
                className="sf-btn-ghost sf-icon-only-btn"
                aria-label={scadentarReturnState?.selectedOverviewInvoiceId ? "Inapoi la factura" : "Inapoi la furnizori"}
                onClick={handleBackToScadentar}
              >
                <ArrowBackRoundedIcon />
              </Button>
            </Tooltip>
            <Tooltip title="AI autocomplete date furnizor">
              <span>
                <Button
                  variant="outlined"
                  className="sf-btn-ghost sf-ai-autofill-btn"
                  aria-label="AI autocomplete date furnizor"
                  startIcon={aiAutofillBusy ? <CircularProgress size={16} /> : <AutoAwesomeRoundedIcon />}
                  disabled={aiAutofillBusy || saving}
                  onClick={handleAiAutofillVendor}
                >
                  AI
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Auto logo web">
              <Button
                variant="outlined"
                className="sf-btn-ghost sf-icon-only-btn"
                aria-label="Auto logo web"
                onClick={async () => {
                  try {
                    setError("");
                    await autoFetchVendorLogo(vendor.id, {});
                    await loadAll();
                  } catch (err) {
                    const msg =
                      err?.response?.data?.error ||
                      err?.message ||
                      "Autofetch logo failed";
                    if (/Nu am domeniu furnizor/i.test(msg)) {
                      setManualLogoUrl(vendor.website || "");
                      setLogoDialogOpen(true);
                    } else {
                      setError(msg);
                    }
                  }
                }}
              >
                <AutoFixHighRoundedIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Logo din link">
              <Button
                variant="outlined"
                className="sf-btn-ghost sf-icon-only-btn"
                aria-label="Logo din link"
                onClick={() => setLogoDialogOpen(true)}
              >
                <UploadFileRoundedIcon />
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              className="sf-btn-primary"
              startIcon={<SaveRoundedIcon />}
              onClick={async () => {
                setSaving(true);
                try {
                  await updateVendor(vendor.id, vendor);
                  await loadAll();
                } catch (err) {
                  setError(err?.response?.data?.error || err.message || "Save failed");
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              Salveaza KYC
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={logoDialogOpen}
        onClose={() => setLogoDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Seteaza logo furnizor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
            Auto logo nu a gasit domeniu. Introdu un link direct catre imaginea logo si apasa Save.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Logo URL"
            placeholder="https://..."
            value={manualLogoUrl}
            onChange={(e) => setManualLogoUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoDialogOpen(false)}>Anuleaza</Button>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            disabled={!String(manualLogoUrl || "").trim()}
            onClick={async () => {
              try {
                setError("");
                await uploadVendorLogoFromUrl(vendor.id, { logo_url: manualLogoUrl });
                setLogoDialogOpen(false);
                setManualLogoUrl("");
                await loadAll();
              } catch (err) {
                setError(
                  err?.response?.data?.error ||
                    err?.message ||
                    "Upload logo din link failed"
                );
              }
            }}
          >
            Save logo
          </Button>
        </DialogActions>
      </Dialog>

      <Paper className="sf-form-card sf-vendor-kyc-grid">
        <TextField label="Denumire" value={vendor.denumire || ""} onChange={(e) => setVendor((s) => ({ ...s, denumire: e.target.value }))} />
        <TextField label="Cod fiscal" value={vendor.cod_fiscal || ""} onChange={(e) => setVendor((s) => ({ ...s, cod_fiscal: e.target.value }))} />
        {isSpvImportedVendor ? (
          <TextField
            label="Tip furnizor (default)"
            value="SPV ANAF"
            InputProps={{ readOnly: true }}
          />
        ) : (
          <TextField
            select
            label="Tip furnizor"
            value={vendor.vendor_scope || "INTRA_EU"}
            onChange={(e) => setVendor((s) => ({ ...s, vendor_scope: e.target.value }))}
          >
            {vendorScopeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Categorie furnizor"
          value={vendor.vendor_category || "SERVICII"}
          onChange={(e) => setVendor((s) => ({ ...s, vendor_category: e.target.value }))}
        >
          {vendorCategoryOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField label="Reg. comert" value={vendor.reg_comertului || ""} onChange={(e) => setVendor((s) => ({ ...s, reg_comertului: e.target.value }))} />
        <TextField label="Cod furnizor" value={vendor.cod_furnizor || ""} onChange={(e) => setVendor((s) => ({ ...s, cod_furnizor: e.target.value }))} />
        <TextField label="Adresa" value={vendor.adresa || ""} onChange={(e) => setVendor((s) => ({ ...s, adresa: e.target.value }))} />
        <TextField label="Localitate" value={vendor.localitate || ""} onChange={(e) => setVendor((s) => ({ ...s, localitate: e.target.value }))} />
        <TextField label="Judet" value={vendor.judet || ""} onChange={(e) => setVendor((s) => ({ ...s, judet: e.target.value }))} />
        <TextField label="Tara" value={vendor.tara || "RO"} onChange={(e) => setVendor((s) => ({ ...s, tara: e.target.value }))} />
        <TextField label="Persoana contact" value={vendor.persoana_contact || ""} onChange={(e) => setVendor((s) => ({ ...s, persoana_contact: e.target.value }))} />
        <TextField label="Email" value={vendor.email || ""} onChange={(e) => setVendor((s) => ({ ...s, email: e.target.value }))} />
        <TextField label="Telefon" value={vendor.telefon || ""} onChange={(e) => setVendor((s) => ({ ...s, telefon: e.target.value }))} />
        <TextField label="Website" value={vendor.website || ""} onChange={(e) => setVendor((s) => ({ ...s, website: e.target.value }))} />
        <TextField label="Banca" value={vendor.banca || ""} onChange={(e) => setVendor((s) => ({ ...s, banca: e.target.value }))} />
        <TextField label="IBAN" value={vendor.iban || ""} onChange={(e) => setVendor((s) => ({ ...s, iban: e.target.value }))} />
        <TextField
          label="KYC notes"
          multiline
          minRows={2}
          value={vendor.kyc_notes || ""}
          onChange={(e) => setVendor((s) => ({ ...s, kyc_notes: e.target.value }))}
          className="sf-grid-span-2"
        />
      </Paper>

      <Box className="sf-kpi-grid">
        <Paper className="sf-kpi-card" style={{ "--sf-accent": "#38bdf8" }}>
          <Typography className="sf-kpi-label">Facturi total</Typography>
          <Typography className="sf-kpi-value">{Number(summary?.invoices_count || 0)}</Typography>
        </Paper>
        <Paper className="sf-kpi-card" style={{ "--sf-accent": "#f59e0b" }}>
          <Typography className="sf-kpi-label">Total de plata</Typography>
          <Typography className="sf-kpi-value">{Number(summary?.total_de_plata_ron || 0).toFixed(2)} RON</Typography>
        </Paper>
        <Paper className="sf-kpi-card" style={{ "--sf-accent": "#22c55e" }}>
          <Typography className="sf-kpi-label">Achitat</Typography>
          <Typography className="sf-kpi-value">{Number(summary?.total_achitat_ron || 0).toFixed(2)} RON</Typography>
        </Paper>
        <Paper className="sf-kpi-card" style={{ "--sf-accent": "#ef4444" }}>
          <Typography className="sf-kpi-label">Overdue</Typography>
          <Typography className="sf-kpi-value">{Number(summary?.overdue_ron || 0).toFixed(2)} RON</Typography>
        </Paper>
      </Box>

      <Paper className="sf-table-shell sf-docs-shell">
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          className="sf-section-head"
        >
          <Typography className="sf-section-title">
            <DescriptionRoundedIcon fontSize="small" />
            Documente furnizor (NDA / Contracte)
          </Typography>
          <Chip
            size="small"
            className="sf-head-chip"
            label={`${documents.length} documente`}
          />
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} gap={1} className="sf-inline-actions">
          <TextField
            select
            size="small"
            label="Tip"
            value={docMeta.doc_type}
            onChange={(e) => setDocMeta((s) => ({ ...s, doc_type: e.target.value }))}
          >
            {docTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Titlu" value={docMeta.title} onChange={(e) => setDocMeta((s) => ({ ...s, title: e.target.value }))} />
          <TextField size="small" label="Nota" value={docMeta.note} onChange={(e) => setDocMeta((s) => ({ ...s, note: e.target.value }))} />
          <Button component="label" variant="outlined" className="sf-btn-ghost" startIcon={<UploadFileRoundedIcon />}>
            Alege fisier
            <input hidden type="file" onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
          </Button>
          {docFile ? <Chip size="small" color="info" label={docFile.name} /> : null}
          <Button
            variant="contained"
            className="sf-btn-primary"
            disabled={!docFile}
            onClick={async () => {
              if (!docFile) return;
              await uploadVendorDocument(vendor.id, { file: docFile, ...docMeta });
              setDocFile(null);
              setDocMeta({ doc_type: "OTHER", title: "", note: "" });
              await loadAll();
            }}
          >
            Upload
          </Button>
        </Stack>

        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Titlu</TableCell>
              <TableCell>Document</TableCell>
              <TableCell align="right">Actiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{String(d.created_at || "").slice(0, 10)}</TableCell>
                <TableCell>{d.doc_type}</TableCell>
                <TableCell>{d.title || "-"}</TableCell>
                <TableCell>
                  <a className="sf-doc-link" href={getVendorDocumentFileUrl(vendor.id, d.id)} target="_blank" rel="noreferrer">
                    Deschide
                  </a>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      className="sf-doc-action-btn"
                      disabled={docBusyId === d.id}
                    >
                      Re-upload
                      <input
                        hidden
                        type="file"
                        onChange={async (e) => {
                          const file = e.target.files?.[0] || null;
                          if (!file) return;
                          try {
                            setDocBusyId(d.id);
                            await reuploadVendorDocumentFile(vendor.id, d.id, { file });
                            await loadAll();
                          } finally {
                            setDocBusyId(null);
                            e.target.value = "";
                          }
                        }}
                      />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      className="sf-doc-delete-btn"
                      startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                      disabled={docBusyId === d.id}
                      onClick={async () => {
                        if (!window.confirm("Stergi acest document?")) return;
                        try {
                          setDocBusyId(d.id);
                          await deleteVendorDocument(vendor.id, d.id);
                          await loadAll();
                        } finally {
                          setDocBusyId(null);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!documents.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box className="sf-empty-state">Nu exista documente. Incarca primul NDA/contract.</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box className={`sf-vendor-history-split ${selectedInvoice ? "has-panel" : ""}`}>
        <Paper className="sf-table-shell sf-history-shell">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            className="sf-section-head"
          >
            <Typography className="sf-section-title">
              <HistoryRoundedIcon fontSize="small" />
              Istoric facturi furnizor
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip size="small" className="sf-head-chip" label={`${invoices.length} facturi`} />
              <Chip
                size="small"
                className="sf-head-chip sf-head-chip-accent"
                label={`Rest ${Number(summary?.total_de_plata_ron || 0).toFixed(2)} RON`}
              />
            </Stack>
          </Stack>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Factura</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Scadenta</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total RON</TableCell>
                <TableCell>Rest RON</TableCell>
                <TableCell align="right">Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((i) => {
                const active = Number(selectedInvoice?.id || 0) === Number(i.id);
                return (
                  <TableRow
                    key={i.id}
                    hover
                    className={`sf-clickable-row sf-vendor-invoice-row ${active ? "is-active" : ""}`}
                    onClick={() => setSelectedInvoice(i)}
                  >
                    <TableCell>{getInvoiceDisplayNumber(i)}</TableCell>
                    <TableCell>{formatProfileDate(i.data_emitere)}</TableCell>
                    <TableCell>{formatProfileDate(i.termen_scadent)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={i.status}
                        className={`sf-status-chip sf-status-${String(i.status || "").toLowerCase()}`}
                      />
                    </TableCell>
                    <TableCell>{formatProfileMoney(i.suma_ron, "RON")}</TableCell>
                    <TableCell>{formatProfileMoney(i.rest_plata_ron, "RON")}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={active ? "contained" : "outlined"}
                        className={active ? "sf-btn-primary sf-invoice-open-btn" : "sf-invoice-open-btn"}
                        endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedInvoice(i);
                        }}
                      >
                        Deschide
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!invoices.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box className="sf-empty-state">Nu exista facturi pentru acest furnizor.</Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        {selectedInvoice ? (
          <Box className="sf-vendor-invoice-panel-slot">
            <Paper className="sf-overview-panel sf-vendor-invoice-panel">
              <Box className="sf-overview-panel-head">
                <Box>
                  <Typography className="sf-side-title">
                    <ReceiptLongRoundedIcon fontSize="small" />
                    Factura {getInvoiceDisplayNumber(selectedInvoice)}
                  </Typography>
                  <Typography className="sf-side-line">
                    Emisa {formatProfileDate(selectedInvoice.data_emitere)} - Scadenta {formatProfileDate(selectedInvoice.termen_scadent)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Chip
                    size="small"
                    label={selectedInvoice.status}
                    className={`sf-status-chip sf-status-${String(selectedInvoice.status || "").toLowerCase()}`}
                  />
                  <IconButton
                    size="small"
                    className="sf-panel-head-icon"
                    onClick={() => setSelectedInvoice(null)}
                    aria-label="Inchide detalii factura"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              {invoicePanelLoading ? (
                <Box className="sf-vendor-invoice-loading">
                  <CircularProgress size={22} />
                  <Typography className="sf-side-line">Se incarca detaliile...</Typography>
                </Box>
              ) : null}
              {invoicePanelError ? <Alert severity="error">{invoicePanelError}</Alert> : null}

              <Box className="sf-amount-grid sf-vendor-invoice-amount-grid">
                <Box className="sf-amount-card">
                  <Typography className="sf-amount-label">Total RON</Typography>
                  <Typography className="sf-amount-value">{formatProfileMoney(selectedInvoice.suma_ron, "RON")}</Typography>
                </Box>
                <Box className="sf-amount-card sf-amount-card-warn">
                  <Typography className="sf-amount-label">Rest RON</Typography>
                  <Typography className="sf-amount-value">{formatProfileMoney(selectedInvoice.rest_plata_ron, "RON")}</Typography>
                </Box>
              </Box>

              {String(selectedInvoice.valuta || "RON").toUpperCase() !== "RON" ? (
                <Box className="sf-side-card sf-right-card-v2">
                  <Typography className="sf-side-line">
                    Valuta: {formatProfileMoney(selectedInvoice.suma_valuta, selectedInvoice.valuta)}
                  </Typography>
                  <Typography className="sf-side-line">
                    Curs: {selectedInvoice.curs_bnr ? Number(selectedInvoice.curs_bnr).toFixed(6) : "-"}
                    {selectedInvoice.curs_date ? ` - ${formatProfileDate(selectedInvoice.curs_date)}` : ""}
                  </Typography>
                </Box>
              ) : null}

              <Box className="sf-side-card sf-right-card-v2">
                <Typography className="sf-side-title">Detalii</Typography>
                <Box className="sf-invoice-detail-grid">
                  <Typography className="sf-side-line">Tip: {selectedInvoice.tip_plata || "-"}</Typography>
                  <Typography className="sf-side-line">Luna: {selectedInvoice.luna || "-"}</Typography>
                  <Typography className="sf-side-line">Sursa: {selectedInvoice.source || "-"}</Typography>
                  <Typography className="sf-side-line">Index incarcare: {selectedInvoice.anaf_index_incarcare || selectedInvoice.anaf_id_solicitare || "-"}</Typography>
                </Box>
                {hasInvoiceProof(selectedInvoice) ? (
                  <Button
                    variant="outlined"
                    className="sf-invoice-proof-btn"
                    startIcon={<DescriptionRoundedIcon fontSize="small" />}
                    onClick={() => window.open(getInvoiceProofUrl(selectedInvoice.id), "_blank", "noopener,noreferrer")}
                  >
                    Deschide PDF
                  </Button>
                ) : null}
              </Box>

              <Box className="sf-side-card sf-right-card-v2">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography className="sf-side-title">Linii factura</Typography>
                  <Chip size="small" className="sf-head-chip" label={`${selectedInvoiceLines.length} linii`} />
                </Stack>
                {!selectedInvoiceLines.length ? (
                  <Box className="sf-empty-state sf-empty-state-compact">Nu exista linii salvate.</Box>
                ) : (
                  <Box className="sf-side-list sf-vendor-lines-list">
                    {selectedInvoiceLines.map((line) => (
                      <Box key={line.id} className="sf-side-item">
                        <Typography className="sf-side-line">
                          <strong>{line.item_name || line.item_description || `Linia ${line.line_index || "-"}`}</strong>
                        </Typography>
                        <Typography className="sf-side-line">
                          Cant. {Number(line.quantity || 0).toFixed(2)}
                          {line.unit_code ? ` ${line.unit_code}` : ""} - Pret {Number(line.unit_price || 0).toFixed(2)}
                        </Typography>
                        <Typography className="sf-side-line">
                          Total {formatProfileMoney(line.line_extension_amount, line.currency || selectedInvoice.valuta || "RON")}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box className="sf-side-card sf-right-card-v2">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography className="sf-side-title">Plati</Typography>
                  <Chip size="small" className="sf-head-chip" label={`${selectedPayments.length} inregistrari`} />
                </Stack>
                {!selectedPayments.length ? (
                  <Box className="sf-empty-state sf-empty-state-compact">Nu exista plati inregistrate.</Box>
                ) : (
                  <Box className="sf-side-list sf-payments-timeline">
                    {selectedPayments.map((payment) => (
                      <Box key={payment.id} className="sf-side-item">
                        <Typography className="sf-side-line">
                          {formatProfileDate(payment.payment_date)} - {formatProfileMoney(payment.amount_ron, "RON")}
                        </Typography>
                        <Typography className="sf-side-line">
                          OP: {payment.op_number || "-"} {payment.note ? `- ${payment.note}` : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box className="sf-side-card sf-right-card-v2">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography className="sf-side-title">Istoric status</Typography>
                  <Chip size="small" className="sf-head-chip" label={`${selectedStatusAudit.length} schimbari`} />
                </Stack>
                {!selectedStatusAudit.length ? (
                  <Box className="sf-empty-state sf-empty-state-compact">Nu exista schimbari manuale.</Box>
                ) : (
                  <Box className="sf-side-list">
                    {selectedStatusAudit.map((item) => (
                      <Box key={item.id} className="sf-side-item">
                        <Typography className="sf-side-line">
                          {String(item.from_status || "-")} -&gt; {String(item.to_status || "-")}
                        </Typography>
                        <Typography className="sf-side-line">
                          {formatProfileDate(item.changed_at)} - {item.changed_by || "manual-ui"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default ScadentarFurnizorProfilePage;
